import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, Headphones } from "lucide-react";

export default function ContinueListening() {
  const { user } = useAuth();
  const { current, playing, play, toggle } = usePlayer();
  const [ep, setEp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setEp(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/last-played");
        if (!cancelled) setEp(data?.episode || null);
      } catch { /* ignored */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !ep || !ep.unlocked || !ep.audio_url) return null;

  const isCurrent = current?.number === ep.number;
  const showPause = isCurrent && playing;
  const onClick = () => {
    if (isCurrent) toggle();
    else play(ep, { startAt: ep.position || 0 });
  };

  // Pretty timestamp
  const fmtTime = (sec) => {
    if (!sec || sec < 1) return null;
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };
  const ts = fmtTime(ep.position);

  return (
    <div
      className="relative bg-ink border border-hotpink/60 shadow-[4px_4px_0px_#FF007F] p-4 md:p-5 flex items-center gap-4 max-w-2xl"
      data-testid="continue-listening-banner"
    >
      <div className="w-12 h-12 md:w-14 md:h-14 bg-hotpink/10 border border-hotpink/40 flex items-center justify-center flex-shrink-0">
        <Headphones className="text-hotpink" size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="scribble text-hotpink text-lg md:text-xl rotate-[-1deg] inline-block leading-none mb-1">welcome back, {user.name}</p>
        <p className="font-outfit text-white text-sm md:text-base leading-tight">
          Continue listening to{" "}
          <span className="font-bold">Episode {ep.number}</span>
          <span className="text-white/60"> — {ep.title}</span>
        </p>
        {ts && (
          <p className="font-outfit text-white/40 text-xs uppercase tracking-widest mt-1" data-testid="continue-resume-stamp">
            resumes at {ts}
          </p>
        )}
      </div>
      <button
        onClick={onClick}
        data-testid="continue-listening-play"
        aria-label={showPause ? "Pause" : "Resume"}
        className="w-12 h-12 md:w-14 md:h-14 bg-hotpink hover:bg-hotpink_dark text-white flex items-center justify-center flex-shrink-0 shadow-[3px_3px_0px_#FFFFFF] hover:shadow-[5px_5px_0px_#FFFFFF] transition-all"
      >
        {showPause ? <Pause size={20} fill="white" /> : <Play size={20} fill="white" />}
      </button>
    </div>
  );
}
